import { IBusStop, IBusStopLite } from "@/domain/entities/busStop";
import {
  IFavoriteLine,
  IFavoriteLineData,
} from "@/domain/entities/favoriteLine";
import {
  IBookInterval,
  IBusLocation,
  IContact,
  IDownloadedFile,
  IFullSeatLine,
  ILineDistrict,
  ILineFareInfo,
  ILineInfo,
  ILineInfoLite,
  ILineType,
  ITurn,
  ITurnLite,
} from "@/domain/entities/lineInfo";
import { IResultData } from "@/domain/entities/shared/result";
import { Result } from "neverthrow";
import { HttpError } from "../shared/errors/httpError";
import { ParseError } from "../shared/errors/parseError";
import {
  ArrivalTimeOptionCodeType,
  BookIntervalCode,
  LineTypeCode,
  WeekdayCode,
} from "@/types";
import { IOnBoardLineData } from "@/domain/entities/onBoardLine/onBoardLine.types";
import {
  FilterKeyValueType,
  IFilter,
  ILineSearchFilter,
  IOption,
} from "@/domain/entities/filter/filter.types";
import { IBusStopLayoutInfo } from "@/domain/entities/lineInfo/busStopLayout.types";
import { IArrivalTime } from "@/domain/entities/lineInfo/arrivalTime.types";
import { IFavoriteBusStop } from "@/domain/entities/favoriteBusStop";
import { IRoadEventsCollection } from "@/domain/entities/roadEvent";

export type HttpEarlyDriveInfo = {
  weekdayCode: WeekdayCode;
  earlyMinute: number;
};

export type HttpPath = [number, number];

export type HttpLineSet = {
  lineSetCodeuid: string;
  name: string;
  shortName?: string;
  todayYn: string;
};

export interface HttpPathGroup {
  lineTurnUid: string;
  useBeginDtm: string;
  useEndDtm?: string;
  path: HttpPath[];
}

export interface HttpBusStopByTurnPath {
  useBeginDtm: string;
  useEndDtm?: string;
  stations: HttpBusStop[];
}

export type HttpTurn = {
  uid: string;
  no: number;
  name: string;
  lineTimeCode?: string;
  lineTimeName?: string;
  stations: HttpBusStop[];
  beginStation: HttpBusStop;
  endStation: HttpBusStop;
  lineSets?: HttpLineSet[];
  earlyDriveWeekly?: HttpEarlyDriveInfo[];
  detourPaths?: HttpPath[];
  fares?: HttpFare[];
  bookYn: boolean;
  buses?: HttpBus[];
  bookIntervals?: HttpBookInterval[];
  useBeginDtm: string;
  useEndDtm?: string;
  stationsArray?: HttpBusStopByTurnPath[];
  paths?: HttpPathGroup[];
  props?: {
    path: HttpPath[];
  };
};

export type HttpLineInfo = {
  uid: string;
  no: string;
  name: string;
  campusCode: string;
  campusName: string;
  lineDirectionCode: string;
  lineDirectionName: string;
  lineDistrictCode: string;
  lineDistrictName: string;
  lineTypeCode: LineTypeCode;
  lineGradeCode: string;
  districtName?: string;
  turns: HttpTurn[];
  fares: HttpFare[];
  bookIntervals?: HttpBookInterval[];
  detourBeginDtm?: string;
  detourEndDtm?: string;
  detourReason?: string;
};

export enum HttpBookDateCode {
  FIXED = "BDC-FIXED", //고정일자
  DAYAFT = "BDC-DAYAFT", // 매월 특정일에 다음날 ~ 다음달 오픈일까지 예약오픈
  DAY = "BDC-DAY", // 매월 특정일에 다음달 예약오픈
  FIRSTDAY = "BDC-1STDAY", // 월첫운행일
  BOARD = "BDC-BOARD", // 탑승일기준
  BEGIN = "BDC-BEGIN", // 기점출발기준
  END = "BDC-END", // 종점도착기준
  WEEK = "BDC-WEEKDA", // 요일.매주O요일(오픈)"
  WINDOW = "BDC-WINDOW", // 고정일간격 0일단위
  NONE = "BDC-NONE", // 조건없음.종점도착일자(마감,취소)
}

export enum HttpBookTimeCode {
  FIXED = "BTC-FIXED", //고정시각
  NONE = "BTC-NONE", // 없음
  BEGIN = "BTC-BEGIN", // 기점출발
  END = "BTC-END", // 종점도착
  BOARD = "BTC-BOARD", // 승차장출발
}

export type HttpBookInterval = {
  memberSeatAssignYn?: boolean;
  lineTurnUid?: string;
  lineRouteNo?: string;
  lineRouteName?: string;
  lineTypeCode?: LineTypeCode;
  bookIntervalCode: BookIntervalCode;
  bookIntervalName: string;
  bookBeginDateCode: HttpBookDateCode;
  bookBeginYear: number;
  bookBeginMonth: number;
  bookBeginWeek?: number; // -1
  bookBeginWeekdayCode?: string; // WKD-FRI
  bookBeginDay: number;
  bookBeginTimeCode: HttpBookTimeCode;
  bookBeginHour: number;
  bookBeginMin: number;
  bookEndDateCode: HttpBookDateCode;
  bookEndYear: number;
  bookEndMonth: number;
  bookEndDay: number;
  bookEndTimeCode: HttpBookTimeCode;
  bookEndHour: number;
  bookEndMin: number;
  bookCancelDateCode: HttpBookDateCode;
  bookCancelYear: number;
  bookCancelMonth: number;
  bookCancelDay: number;
  bookCancelTimeCode: HttpBookTimeCode;
  bookCancelHour: number;
  bookCancelMin: number;
  bookPointCode?: string;
  displayOrder?: number;
};

export type HttpFare = {
  bookIntervalCode: string;
  fareAmount: number;
  unitYn: boolean;
  lineTurnUid?: string;
};

export type HttpTRansRoute = {
  uid: string;
  no: string;
  name: string;
};

export type HttpTrans = {
  routes: HttpTRansRoute[];
  transText: string;
};

export type HttpBusStop = {
  uid: string; // 정류장 식별자
  no: string; // 정류장 번호
  name: string; // 정류장 이름
  shortName: string;
  lat: number; // 정류장 위도
  lng: number; // 정류장 경도
  arrivalTime: string; // 출발시간 (노선과 결합할 때)
  departureTime: string; // 도착시간 (노선과 결합할 때)
  onYn: boolean; // 승차장 여부 (노선 중에 하나라도 true 이면 true)
  offYn: boolean; // 하차장 여부 (노선 중에 하나라도 true 이면 true)
  beginYn: boolean; // 기점 여부 (노선 중에 하나라도 true 이면 true)
  endYn: boolean; // 종점 여부 (노선 중에 하나라도 true 이면 true)
  coreYn: boolean; // 주요 정류장 여부 (노선 중에 하나라도 true 이면 true)
  finalYn: boolean; // 결점 여부 (노선 중에 하나라도 true 이면 true)
  transYn: boolean; // 환승 정류장 여부 (노선 중에 하나라도 true 이면 true)
  returnYn: boolean; // 회차 정류장 여부
  streetViewYn: boolean; // 거리뷰 표시 여부
  displayYn: boolean; // 화면 표시 여부
  useYn?: boolean; // 사용 여부(사용시작일시, 사용종료일시로 계산된 값)
  omittedYn?: boolean; // 미경유 여부
  tempYn?: boolean; // 임시정류장 여부
  distanceM: number; // 미터 단위 직선 거리 (내 위치와 결합할 때)
  useStationLayoutYn: boolean;
  trans?: HttpTrans;
  isFavorite?: boolean;
  useBeginDtm: string; // 사용시작일시
  useEndDtm?: string; // 사용종료일시
};

export type HttpFile = {
  fileUid: string;
  fileName?: string;
  fileSubTypeCode?: string;
  fileSubTypeCodeName?: string;
  fileUrl?: string;
};

export type HttpBus = {
  allocUid: string;
  busOrder: number;
  carNo?: string;
  vendorName?: string;
  seatCount: number;
  bookCount: number;
  boardCount: number;
  remainCount: number;
  allocStatusCode: string;
  busImages?: HttpFile[];
};

export type HttpFavoriteLine = {
  uid: string; // 노선회차 식별자
  name: string; // 노선회차 이름
  stations?: HttpBusStop[];
  onStation?: {
    uid: string;
  };
  offStation?: {
    uid: string;
  };
  onNoticeYn: boolean;
  offNoticeYn: boolean;
  route?: {
    uid: string;
    no: string;
    name: string;
    lineGradeCode: string;
    lineTypeCode: LineTypeCode;
  };
  fares: HttpFare[];
  bookIntervals?: HttpBookInterval[];
  earlyDriveWeekly?: HttpEarlyDriveInfo[];
  buses?: HttpBus[];
  gateNoList?: string;
  lineSets?: HttpLineSet[];
  detourBeginDtm?: string;
  detourEndDtm?: string;
  detourReason?: string;
};

export type HttpBusLocation = {
  allocUid: string;
  lineTurnUid: string;
  lineRouteUid: string;
  lineTypeCode: string;
  driveDate: string;
  carNo: string;
  busOrder: number;
  lastSeat?: {
    seatCount: number;
    remainCount: number;
    boardCount: number;
  };
  lastLocation?: {
    lat: number;
    lng: number;
    bearing?: number;
    driveEventCode: string;
    driveEventName: string;
  };
  allocStatusCode?: string;
  collectedTs: string;
  stationEvents?: HttpBusStopEvent[];
};

export type HttpBusStopEvent = {
  uid: string;
  name: string; // 정류장 이름
  shortName: string; // 정류장 단축이름 ("야탑")
  lat: number; // 정류장 위도
  lng: number; // 정류장 경도
  /** 방위각 */
  bearing?: number;
  arrivalTime: string; // 정류장 도착 예정 시각 (조기 출발 고려 안함)
  departureTime: string; // 정류장 출발 예정 시각 (조기 출발 고려 안함)
  minutes: number;
  collectedTs: string; // 이벤트 수집 일시 ("2022-05-02T16:10:10.828+09:00")
  stationEventCode?: HttpBusStopEventCode; // 정류장 이벤트. SEV-ENTER:진입, SEV-ARRIVAL:도착, SEV-LEAVE:진출
  prevIntervalMin: number; // 이전 정류장과의 분단위 간격
  displayYn?: boolean;
};

export enum HttpBusStopEventCode {
  Enter = "SEV-ENTER",
  Arrive = "SEV-ARRIVE",
  Leave = "SEV-LEAVE",
}

export type HttpLineType = {
  lineTypeCode: string;
  lineTypeName: string;
  displayOrder: number;
};

export type HttpLineFare = {
  fareAmount: number;
  bookIntervalCode?: string; // BDT-MONTH
  maxBookCount: number;
  unitAmountYn?: boolean;
  maxBookSeatCount?: number;
  unitCancelAmount?: number;
  code?: string; // FTG-ADULT, FTG-OLD
  name?: string; // 성인, 경로
  displayOrder?: number;
};

export type HttpLineDistrict = {
  districtName: string;
};

export interface HttpFullSeatLineTurn {
  uid: string;
  no: string;
  name: string;
  lineSets?: HttpLineSet[];
  earlyDriveWeekly?: HttpEarlyDriveInfo[];
}
export interface HttpFullSeatLine {
  uid: string; // "202505120733510840278952057LNR",
  no: string; // "노원(조기출근)",
  name: string; // "노원(조기출근)",
  lineTypeCode: LineTypeCode; // "LRT-FORWAR",
  lineTypeName: string;
  displayOrder: string; // 1080,
  displayMethodCode: string; // "LDM-NONE",
  campusCode: string; // "202305151930087369993586635CPS",
  campusName: string; // "이천캠퍼스",
  lineDirectionCode: string; // "202305151944232502110868106LDR",
  lineDirectionName: string; // "출근",
  lineDistrictCode: string; // "202305151944232502110868111LDT",
  lineDistrictName: string; // "서울(강북)",
  useStationLayoutYn: boolean; // false
  turns: HttpFullSeatLineTurn[];
}

export interface HttpOnBoardLine {
  serviceUid: string; // 202307031710447612665133592SVC
  memberUid: string; // 202307071118120741481706595MBR
  allocUid: string; // 202308211322252541069779891ALC
  boardUid: string; // 202309131005524722258269999BRD
  lineRouteUid: string;
  lineRouteNo: string; // 102
  lineRouteName: string; // 고덕동
  lineTurnUid: string; // 202307040000000000000000002LNT
  lineTypeCode: string; // LRT-BACKWA
  lineTypeName: string; // 복귀행
  campusCode: string; // 202305151930087369993586635CPS
  campusName: string; // 이천캠퍼스
  lineDirectionCode: string; // 202305151944232502110868105LDR
  lineDirectionName: string; // 퇴근
  lineDistrictCode: string; // 202305151944232502110868111LDT
  lineDistrictName: string; // 서울(강북)
  lineTimeCode: string; // 202305151942514536424967773LTM
  lineTimeName: string; // 통상
  beginLineStationUid: string; // 202307171329345173699556182STA
  beginLineStationName: string; // 하이닉스 통근버스 주차장
  expectedBeginDtm: string; // 2023-09-13T10:10:00.000000000+09:00
  endLineStationUid: string; // 202307040000000000000000005STA
  endLineStationName: string; // 하이닉스 테스트정류장5
  expectedEndDtm: string; // 2023-09-13T10:50:00.000000000+09:00
  expectedOnStationUid?: string;
  expectedOffStationUid?: string;
  realEndDtm: string; // 2023-09-14T10:50:00.000000000+09:00
  realOnStationUid: string; // 202307040000000000000000002STA
  realOnStationName: string; // 하이닉스 테스트정류장2
  realOnDtm: string; // 2023-09-13T10:06:16.000000000+09:00
  carNo: string; // 서울25고5766
  stations: HttpBusStop[];
  earlyDriveWeekly: HttpEarlyDriveInfo[];
  lineSets?: HttpLineSet[];
  requestOffStationUid?: string; // 202402071836548944839827032STA
  detourBeginDtm?: string;
  detourEndDtm?: string;
  detourReason?: string;
}

export interface HttpOnBoarLineData {
  onBoardInfo?: HttpOnBoardLine;
  fullSeatLineRouteCount?: number;
}

export type HttpBusStopLayout = {
  onGateYn: boolean;
  floorNo: number;
  rowNo: number;
  columnNo: number;
  gateNo: string; // A1
  gateNoGroup: string; // A Line
};

export type HttpBusStopLayoutInfo = {
  lineTurnUid: string; // 202307040000000000000000002LNT
  lineStationUid: string; // 202307171513341645370025589STA
  lineStationName: string; // 하이닉스 고담 주차장
  arrivalTime: string; // 10:30
  departureTime: string; // 10:30
  stationOrder: number;
  onYn: boolean;
  offYn: boolean;
  returnYn: boolean;
  coreYn: boolean;
  transYn: boolean;
  beginYn: boolean;
  finalYn: boolean;
  endYn: boolean;
  gateNo: string[];
  campusCode: string; // 202305151930087369993586635CPS
  campusName: string; // 이천캠퍼스
  stationLayout: HttpBusStopLayout[][][];
};

export interface HttpViaPointRequestParams {
  viaPointId: string; // "봇들3단지.판교신미주아파트"
  viaPointName: string; // "봇들3단지.판교신미주아파트"
  viaX: string; // "127.12888404668372"
  viaY: string; // "37.41067438791328"
  viaDetailAddress: string; // ""
  viaPoiId: string; // ""
  viaTime: string; // ""
  wishStartTime: string; // ""
  wishEndTime: string; // "
}

export interface HttpTmapArrivalTimeRequestParams {
  allocUid: string;
  reqCoordType: string; // "WGS84GEO"
  resCoordType: string; // "WGS84GEO"
  startName: string; // "%EB%B4%87%EB%93%A43%EB%8B%A8%EC%A7%80.%ED%8C%90%EA%B5%90%EC%8B%A0%EB%AF%B8%EC%A3%BC%EC%95%84%ED%8C%8C%ED%8A%B8" (UTF-8로 인코딩되어야함)
  startX: string; // "127.12888404668372"
  startY: string; // "37.41067438791328"
  startTime: string; // "202409231314", // null 이면 현재시간을 넣음
  endName: string; // "%EB%8C%80%EC%9B%90%EC%82%AC%EA%B1%B0%EB%A6%AC.%EA%B9%8C%EC%B9%98%EB%A7%88%EC%9D%84" (UTF-8로 인코딩되어야함)
  endX: string; // "127.12947680717866"
  endY: string; // "37.410380377246206"
  searchOption: string; // "0"
  carType: string; // "1"
  viaPoints: HttpViaPointRequestParams[];
}

export interface HttpTmapArrivalTimeProperties {
  totalDistance: string; // "237"
  totalTime: string; // "103"
  totalFare: string; // "0"
}

export interface HttpTmapArrivalTimeFeatures {
  type: string; // Feature
  geometry: {
    type: string;
    coordinates: [number, number];
  };
  properties: {
    index: string; // "0"
    viaPointId: string;
    viaPointName: string;
    arriveTime: string; // "20240923131400"
    completeTime: string; // "20240923131400"
    distance: string; // "0"
    deliveryTime: string; // "0"
    waitTime: string; // "0"
    pointType: string; // "S" "E" "B1" "B2" ...
  };
}

export type HttpContact = {
  serviceUid: string; // 202307031710447612665133592SVC
  name: string; // 상황실
  officePhone: string; // 1661-0771
  beginHour: number;
  beginMin: number;
  endHour: number;
  endMin: number;
  campusCode: string; // 202305151930087369993586635CPS
  campusName: string; // 이천캠퍼스
};

export interface HttpTmapArrivalTime {
  type: string; // FeatureCollection
  properties: HttpTmapArrivalTimeProperties;
  features: HttpTmapArrivalTimeFeatures[];
}

export interface HttpLineTurnStation {
  lineTurnUid: string; // "202312132359348150874224662LNT",
  arrivalTime: string; // "06:00",
  departureTime: string; // "06:00",
  expectedArrivalDtm: string; // "2024-11-18T06:00:00.000000000+09:00",
  expectedDepartureDtm: string; // "2024-11-18T06:00:00.000000000+09:00",
  stationOrder: number; // 10,
  onYn: boolean;
  offYn: boolean;
  coreYn: boolean;
  transYn: boolean;
  beginYn: boolean;
  finalYn: boolean;
  endYn: boolean;
  internalYn: boolean;
  pastArrivalDtm?: string; // "2024-11-18T05:59:28.361000000+09:00",
  avgArrivalTime?: string; // HH:mm:ss,
  uid: string; // "202312132354149690769234319STA",
  no: string; // "창동역 2번출구 롯데리아 앞 버스정류장",
  name: string; // "창동역 2번출구 롯데리아 앞 버스정류장",
  shortName: string; // "창동역 2번출구 롯데리아 앞 버스정류장",
  lat: number; // 37.6523941,
  lng: number; // 127.0463276,
  streetViewYn: boolean; // true
}

export type HttpLineInfoResponse = {
  data: HttpLineInfo[];
};

export type HttpBusStopLite = {
  uid: string;
  no: string;
  name: string;
  shortName: string;
  gateNoList?: string; // 정류장이 여러개 일 경우 ex) "A1,B13,B15"
};

export type HttpTurnLite = {
  uid: string;
  no: string;
  name: string;
  earlyDriveWeekly?: HttpEarlyDriveInfo[];
  beginStationDepartureTime: string;
  stations: HttpBusStopLite[];
  bookYn: boolean;
  lineSets?: HttpLineSet[];
};

export type HttpLineInfoLite = {
  uid: string;
  no: string;
  name: string;
  lineTypeCode: LineTypeCode;
  displayMethodCode: string;
  turns: HttpTurnLite[];
};

export type HttpLineInfoLiteResponse = {
  data: HttpLineInfoLite[];
};

export type HttpLineInfoDetailResponse = {
  data: HttpLineInfo;
};

export type HttpTurnListResponse = {
  data: HttpTurn[];
};

export type HttpLineTypeResponse = {
  data: HttpLineType[];
};

export type HttpLineDistrictResponse = {
  data: HttpLineDistrict[];
};

export type HttpBusStopResponse = {
  data: HttpBusStop[];
};

export type HttpFavoriteLineResponse = {
  data: HttpFavoriteLine[];
};

export type HttpBusLocationResponse = {
  data: HttpBusLocation[];
};

export type HttpLineFareResponse = {
  data: HttpLineFare[];
};

export type HttpBookIntervalResponse = {
  data: HttpBookInterval[];
};

export type HttpOnBoardLineResponse = {
  data: HttpOnBoarLineData;
};

export type HttpBusStopLayoutInfoResponse = {
  data: HttpBusStopLayoutInfo[];
};

export interface HttpTmapArrivalTimeResponse {
  data: HttpTmapArrivalTime;
}

export interface HttpLineTurnStationListResponse {
  data: HttpLineTurnStation[];
}

export interface HttpFullSeatLineListResponse {
  data: HttpFullSeatLine[];
}

export interface ILineInfoParser {
  toDomain(data: HttpLineInfo): Result<ILineInfo, ParseError>;
}

export interface IBusStopLiteParser {
  toDomain(data: HttpBusStopLite): Result<IBusStopLite, ParseError>;
}

export interface ITurnLiteParser {
  toDomain(data: HttpTurnLite): Result<ITurnLite, ParseError>;
}

export interface ILineInfoLiteParser {
  toDomain(data: HttpLineInfoLite): Result<ILineInfoLite, ParseError>;
}

export interface ITurnParser {
  toDomain(data: HttpTurn): Result<ITurn, ParseError>;
}

export interface ILineTypeParser {
  toDomain(data: HttpLineType): Result<ILineType, ParseError>;
}

export interface ILineDistrictParser {
  toDomain(data: HttpLineDistrict): Result<ILineDistrict, ParseError>;
}

export interface IBusStopParser {
  toDomain(data: HttpBusStop): Result<IBusStop, ParseError>;
}

export interface IFavoriteLineParser {
  toDomain(data: HttpFavoriteLine): Result<IFavoriteLine, ParseError>;
}

export interface IBusLocationParser {
  toDomain(data: HttpBusLocation): Result<IBusLocation, ParseError>;
}

export interface ILineFareParser {
  toDomain(data: HttpLineFare): Result<ILineFareInfo, ParseError>;
}

export interface IBookIntervalParser {
  toDomain(data: HttpBookInterval): Result<IBookInterval, ParseError>;
}

export interface IBusStopLayoutInfoParser {
  toDomain(data: HttpBusStopLayoutInfo): Result<IBusStopLayoutInfo, ParseError>;
}

export interface IOnBoardLineParser {
  toDomain(httpData: HttpOnBoarLineData): Result<IOnBoardLineData, ParseError>;
}

export interface ILineTurnBusStopParser {
  toDomain(
    data: HttpLineTurnStation,
    type: ArrivalTimeOptionCodeType
  ): Result<IArrivalTime, ParseError>;
}

export type HttpBaseFilter<TResponse = any> = TResponse;

export interface HttpLineSearchFilterOption {
  uid: string;
  code: string;
  codeName: string;
}

export interface HttpLineSearchFilter {
  codeGroup: string;
  codeGroupName: string;
  codes: HttpLineSearchFilterOption[];
}

export interface HttpLineSearchFilterResponse {
  data: HttpLineSearchFilter[];
}

export interface ILineSearchFilterParser {
  toDomain(data: HttpLineSearchFilter): Result<ILineSearchFilter, ParseError>;
}

export interface HttpParentCustomCode {
  parentCodeGroup: string;
  parentCodeGroupName: string;
  parentUid: string;
  parentCode: string;
  parentCodeName: string;
  displayOrder: number;
}

export interface HttpChildCustomCode {
  childCodeGroup: string;
  childCodeGroupName: string;
  childUid: string;
  childCode: string;
  childCodeName: string;
  displayOrder: number;
}

export interface HttpBaseFilterWithRelation {
  codeGroup: string;
  codeGroupName: string;
  uid: string;
  code: string;
  codeName: string;
  parentCustomCodes?: HttpParentCustomCode[];
  childCustomCodes?: HttpChildCustomCode[];
}

export type HttpBaseFiltersResponse<TResponse = any> = {
  data: HttpBaseFilter<TResponse>[];
};

export type OptionMapper<TResponse> = (data: HttpBaseFilter<TResponse>) => {
  key: string;
  name: string;
  displayText?: string;
  value?: string;
  optionMetadata?: { [key: string]: any };
};

export type GetOptions<TResponse> = (
  response: HttpBaseFiltersResponse<TResponse>
) => HttpBaseFilter<TResponse>[];

export interface IFilterParser<TResponse = any> {
  toDomain(
    response: HttpBaseFiltersResponse<TResponse>,
    pathParam: string,
    key: string,
    type: FilterKeyValueType,
    optionMapper: OptionMapper<TResponse>,
    multiple?: boolean,
    label?: string,
    dummyOptionName?: string,
    defaultOptionCode?: string,
    parentKey?: string,
    essential?: boolean,
    callback?: (selectedOptions?: IOption[] | IOption) => void,
    getOptions?: GetOptions<TResponse>,
    allowDirectInput?: boolean
  ): Result<IFilter, ParseError>;
}

export type HttpContactListResponse = {
  data: HttpContact[];
};

export interface IContactParser {
  toDomain(data: HttpContact): Result<IContact, ParseError>;
}

export interface ITmapArrivalTimeParser {
  toDomain(
    data: HttpTmapArrivalTime
  ): Result<IArrivalTime[], ParseError | HttpError>;

  fromDomain(busLocation: IBusLocation): HttpTmapArrivalTimeRequestParams;
}

export interface HttpFavoriteBusStop {
  lineStationUid: string;
  name: string;
  shorName: string;
  lat: number;
  lng: number;
  displayOrder: number;
}

export interface HttpFavoriteBusStopParams {
  stationUid: string;
  name: string;
  shorName: string;
  lat: number;
  lng: number;
  displayOrder: number;
}

export interface HttpFavoriteBusStopListResponse {
  data: HttpFavoriteBusStop[];
}

export interface IFavoriteBusStopParser {
  toDomain(data: HttpFavoriteBusStop): Result<IFavoriteBusStop, ParseError>;
  fromDomain(data: IFavoriteBusStop): HttpFavoriteBusStopParams | ParseError;
}

export interface HttpRoadEvent {
  uid: string; // 202408050659008012078331963TTI
  externalIncidentId: string; // L93106245889
  title: string; // [공사] 올림픽대로 올림픽대교 에서 천호대교 방향 4차로 도로공사 천호대로 남단IC 연결체계 개선공사 올림픽대로 천호대로 남단IC 부분통제 서울청
  incidentLaneName: string; // 4차로
  lat: number; // 27.1183282253618
  lng: number; // 7.54102984403462
  useBeginDtm: string; // 2021-07-22T00:00:00.000000000+09:00
  useEndDtm?: string; // 2025-11-14T23:59:00.000000000+09:00
  incidentTypeName: string; // 공사
  incidentGradeName: string; // B등급
  incidentTrafficName: string; // 서행
  incidentBlockName: string; // 없음
}

export interface HttpRoadEventListResponse {
  data: HttpRoadEvent[];
  totalCount: number;
}

export interface IRoadEventsCollectionParser {
  toDomain(
    response: HttpRoadEventListResponse
  ): Result<IRoadEventsCollection, ParseError>;
}

export interface IFullSeatLineParser {
  toDomain(data: HttpFullSeatLine): Result<IFullSeatLine, ParseError>;
}

export interface ILineInfoRepository {
  getAllLineInfos(
    sortOrder?: string
  ): Promise<Result<ILineInfo[], ParseError | HttpError>>;

  getAllLineInfosLite(
    sortOrder?: string
  ): Promise<Result<ILineInfoLite[], ParseError | HttpError>>;
  getAllLineInfosLiteByFilters(
    query: string,
    sortOrder?: string
  ): Promise<Result<ILineInfoLite[], ParseError | HttpError>>;

  getLineInfosByFilters(
    query: string,
    sortOrder?: string
  ): Promise<Result<ILineInfo[], ParseError | HttpError>>;

  getLineInfoDetail(
    routeId: string
  ): Promise<Result<ILineInfo, ParseError | HttpError>>;

  getLineTypes(): Promise<Result<ILineType[], ParseError | HttpError>>;

  getLineDistricts(
    lineTypeCode?: LineTypeCode
  ): Promise<Result<ILineDistrict[], ParseError | HttpError>>;

  getLineTurns(
    searchParms: string
  ): Promise<Result<ITurn[], ParseError | HttpError>>;

  getNearBusStops(
    latitude: number,
    longitude: number,
    radiusMeters: number
  ): Promise<Result<IBusStop[], ParseError | HttpError>>;

  getFavoriteLineInfos(): Promise<
    Result<IFavoriteLine[], ParseError | HttpError>
  >;

  addFavoriteLine(
    favoriteLine: IFavoriteLineData,
    getOnTargetDate?: string,
    getOffTargetDate?: string
  ): Promise<Result<IResultData, ParseError | HttpError>>;

  removeFavoriteLine(
    turnId: string,
    onStationId?: string,
    offStationId?: string
  ): Promise<Result<IResultData, ParseError | HttpError>>;

  getBusLocations(
    routeId: string,
    turnId?: string,
    allocUid?: string
  ): Promise<Result<IBusLocation[], ParseError | HttpError>>;

  getLineFares(
    turnId: string,
    bookIntervalCode?: BookIntervalCode
  ): Promise<Result<ILineFareInfo[], ParseError | HttpError>>;

  getBookIntervals(
    turnId?: string
  ): Promise<Result<IBookInterval[], ParseError | HttpError>>;

  getOnBoardLine(): Promise<Result<IOnBoardLineData, ParseError | HttpError>>;

  getFilter<TResponse = any>(
    pathParam: string,
    key: string,
    type: FilterKeyValueType,
    optionMapper: OptionMapper<TResponse>,
    isPublic: boolean,
    multiple?: boolean,
    label?: string,
    dummyOptionName?: string,
    defaultOptionCode?: string,
    parentKey?: string,
    essential?: boolean,
    callback?: (selectedOptions?: IOption[] | IOption) => void,
    getOptions?: GetOptions<TResponse>,
    allowDirectInput?: boolean
  ): Promise<Result<IFilter, ParseError | HttpError>>;

  getLineSearchFilter(
    searchParams: string
  ): Promise<Result<ILineSearchFilter[], ParseError | HttpError>>;

  getBusStopLayoutInfo(
    turnUid: string,
    busStopUid: string
  ): Promise<Result<IBusStopLayoutInfo, ParseError | HttpError>>;

  downloadFile(
    fileUid: string
  ): Promise<Result<IDownloadedFile, ParseError | HttpError>>;

  getContactList(): Promise<Result<IContact[], ParseError | HttpError>>;

  createTmapMultipleViaPointsGuide(
    busLocation: IBusLocation
  ): Promise<Result<IArrivalTime[], ParseError | HttpError>>;

  getBusStopArrivalTimes(
    lineTurnUid: string,
    busOrder: number,
    driveDate: string,
    type: ArrivalTimeOptionCodeType
  ): Promise<Result<IArrivalTime[], ParseError | HttpError>>;

  getFavoriteBusStops(): Promise<
    Result<IFavoriteBusStop[], ParseError | HttpError>
  >;

  addFavoriteBusStop(
    favoriteBusStop: IFavoriteBusStop
  ): Promise<Result<IResultData, ParseError | HttpError>>;

  removeFavoriteBusStop(
    stationUid: string
  ): Promise<Result<IResultData, ParseError | HttpError>>;

  /** 교통정보 목록 조회 */
  getRoadEventList(
    routeUid: string,
    offset?: number,
    limit?: number
  ): Promise<Result<IRoadEventsCollection, ParseError | HttpError>>;

  /** 만석 노선 리스트 조회 */
  getFullSeatLineList(): Promise<
    Result<IFullSeatLine[], ParseError | HttpError>
  >;
}
